// @ts-nocheck
import React, { useEffect, useReducer, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import classNames from 'classnames';
import device from 'common/support/device';

import { FocusGroup, FocusTrap } from 'components/focus';
import Button from 'components/common/Button';
import LogoPage from 'components/pages/LogoPage';
import DividedContent from 'components/common/DividedContent';

import focusManager from 'support/focus/FocusManager';
import textToSpeechManager from 'support/tts/TextToSpeechManager';
import {
  setDialogOpen,
  setDialogClosed,
  setRequestedFocusId,
} from 'actions/dialog';

import styles from './Dialog.module.scss';

const Dialog = ({
  title,
  message,
  isOpen,
  backgroundUrl,
  positiveCTA,
  onPositive,
  negativeCTA,
  onNegative,
  horizontal,
  injectHtml,
  onActionOverride,
  initialFocus,
}) => {
  const dispatch = useDispatch();

  const isAnotherDialogOpen = useSelector(state => state.dialog.isOpen);
  const [state, localDispatch] = useReducer(
    (prevState, action) => {
      switch (action.type) {
        case 'IS_OPEN_CHANGED':
          return {
            isOpen: isOpen && !isAnotherDialogOpen,
          };
        default:
          return prevState;
      }
    },
    {
      isOpen: isOpen && !isAnotherDialogOpen,
    },
  );

  useEffect(() => {
    localDispatch({ type: 'IS_OPEN_CHANGED' });
  }, [isOpen, localDispatch]);

  const latestFocusId = useRef(null);

  const requestedFocusId = useSelector(
    reduxState => reduxState.dialog.requestedFocusId,
  );
  useEffect(() => {
    latestFocusId.current = requestedFocusId;
  });

  useEffect(() => {
    if (state.isOpen) {
      dispatch(setRequestedFocusId(focusManager.currentFocusId));
    }
  }, [state.isOpen, dispatch]);

  useEffect(() => {
    if (!state.isOpen && latestFocusId.current) {
      focusManager.setCurrentFocus(latestFocusId.current);
    }
  }, [state.isOpen]);

  useEffect(() => {
    if (state.isOpen) {
      dispatch(setDialogOpen());
      textToSpeechManager.clear();
      textToSpeechManager.add(title);
      textToSpeechManager.add(message);

      if (initialFocus === 'negative' && onNegative) {
        focusManager.setCurrentFocus('dialog-negative-button');
      } else if (onPositive) {
        focusManager.setCurrentFocus('dialog-positive-button');
      } else if (onNegative) {
        focusManager.setCurrentFocus('dialog-negative-button');
      }
    }
  });

  useEffect(() => {
    return () => {
      if (state.isOpen) {
        dispatch(setDialogClosed());
      }
    };
  }, [dispatch, state.isOpen]);

  useEffect(() => {
    return () => {
      textToSpeechManager.stop();
    };
  }, []);

  return (
    <Modal
      isOpen={state.isOpen}
      aria={{
        hidden: !device.isSamsungTizen,
      }}
      overlayClassName={classNames(
        styles.overlay,
        'app-scalable',
        'fixed-top fixed-bottom',
        'd-flex justify-content-center align-items-center',
      )}
      className={classNames(
        'text-white',
        'w-100 h-100',
        'd-flex justify-content-center align-items-center',
      )}
      {...(backgroundUrl
        ? { style: { content: { backgroundImage: `url(${backgroundUrl})` } } }
        : {})}
    >
      <FocusTrap
        className={classNames(
          'w-100 h-100',
          'd-flex flex-column align-items-center justify-content-center',
        )}
      >
        <LogoPage className="position-relative">
          <DividedContent title={title}>
            <div
              className={styles.message}
              data-testid="dialog-message"
              // We allow for setting inner HTML in certain cases
              // since there are requirements around allowing dynamic
              // formatting for our dialogues.
              // eslint-disable-next-line react/no-danger
              {...(injectHtml
                ? { dangerouslySetInnerHTML: { __html: message } }
                : {})}
            >
              {!injectHtml ? message : null}
            </div>
            <FocusGroup
              horizontal={horizontal}
              onActionOverride={onActionOverride}
            >
              {onPositive && (
                <Button
                  className={classNames(
                    'dialog-positiveButton',
                    styles.dialogButton,
                  )}
                  onClick={onPositive}
                  size="md"
                  ttsText={positiveCTA}
                  focusId="dialog-positive-button"
                >
                  {positiveCTA}
                </Button>
              )}
              {onNegative && (
                <Button
                  className={classNames(
                    'dialog-negativeButton',
                    styles.dialogButton,
                  )}
                  onClick={onNegative}
                  size="md"
                  ttsText={negativeCTA}
                  focusId="dialog-negative-button"
                >
                  {negativeCTA}
                </Button>
              )}
            </FocusGroup>
          </DividedContent>
        </LogoPage>
      </FocusTrap>
    </Modal>
  );
};

Dialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string,
  message: PropTypes.string,
  backgroundUrl: PropTypes.string,
  positiveCTA: PropTypes.string,
  onPositive: PropTypes.func,
  negativeCTA: PropTypes.string,
  onNegative: PropTypes.func,
  horizontal: PropTypes.bool,
  injectHtml: PropTypes.bool,
  onActionOverride: PropTypes.func,
  initialFocus: PropTypes.oneOf(['positive', 'negative']),
};

Dialog.defaultProps = {
  title: '',
  message: '',
  backgroundUrl: null,
  negativeCTA: 'Back',
  onNegative: null,
  positiveCTA: 'Okay',
  onPositive: null,
  horizontal: false,
  injectHtml: false,
  onActionOverride: () => false,
  initialFocus: 'positive',
};

export default Dialog;
